import styled from 'styled-components';
import { FC } from 'react';
import { FormatDate, FormatHour } from 'src/components/common/FormatDate';
import { useToggle } from 'react-use';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from 'src/components/common/Button';
import { Edit } from 'src/components/icons/Edit';
import { Delete } from 'src/components/icons/Delete';
import { WithConfirmation } from 'src/components/common/Popup/ConfirmationPopup';
import { useTranslate } from 'react-t7e';
import { UserNoteForm } from 'src/modules/materials/components/MaterialPage/MaterialPageTabs/UserNotesTab/UserNoteForm';
import { makeTransition } from 'src/utils/motion/transitions';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { ExpandableContent } from 'src/components/common/ExpandableContent';
import { media } from 'src/styles/designs';
import { Notes } from 'src/graphql/schema.graphql';
import { NeauviaItem } from 'src/models/directus';
import { useMaterialsDeleteUserNoteMutation } from 'src/modules/materials/hooks/useMaterialsDeleteUserNoteMutation';

const Container = styled(motion.div)`
  display: grid;
  grid-template-areas: 'date actions' 'content content';
  gap: 16px;

  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.skeleton.base};

  ${media.w.greaterThanOrEqual.desktop} {
    grid-template-columns: 120px auto 120px;
    grid-template-areas: 'date content actions';
  }
`;

const DateContainer = styled.div`
  grid-area: date;
  font-weight: 300;
  font-size: 12px;
  line-height: 1.25;
`;

const Date = styled.p`
  font-weight: 700;
  text-transform: uppercase;
`;

const ContentColumn = styled.div`
  grid-area: content;
  font-weight: 300;
  font-size: 16px;
  line-height: 1.5;
`;

const FormContainer = styled(motion.div)`
  padding-top: 12px;
`;

const Content = styled.p`
  margin-top: -4px;
  margin-bottom: 16px;
`;

const ActionsContainer = styled.div`
  grid-area: actions;
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;

export const UserNote: FC<{ item: NeauviaItem; note: Notes }> = ({
  item,
  note,
}) => {
  const { date_created, content } = note;

  const [isEditable, toggleIsEditable] = useToggle(false);
  const { _ } = useTranslate();

  const { mutate } = useMaterialsDeleteUserNoteMutation(item);

  return (
    <Container {...makeTransition({ fade: true })}>
      <DateContainer>
        <Date>
          <FormatDate format="dd MMMM yyyy">{date_created}</FormatDate>
        </Date>
        <FormatHour>{date_created}</FormatHour>
      </DateContainer>

      <ContentColumn>
        <AnimatePresence exitBeforeEnter>
          {isEditable ? (
            <FormContainer {...makeTransition({ fade: true })}>
              <UserNoteForm
                key="editable"
                item={item}
                note={note}
                onCancel={() => toggleIsEditable(false)}
                onSubmit={() => toggleIsEditable(false)}
              />
            </FormContainer>
          ) : (
            <ExpandableContent>
              <Content>
                <Markdown allowedTokens={[/\n/]}>{content}</Markdown>
              </Content>
            </ExpandableContent>
          )}
        </AnimatePresence>
      </ContentColumn>

      <ActionsContainer>
        <Button
          kind="circled"
          size="small"
          icon={Edit}
          onClick={() => toggleIsEditable()}
        />
        <WithConfirmation
          title={_('Delete note?')}
          description={_('Are you sure you want to delete the note?')}
          submitText={_('Yes, delete')}
          onConfirm={() => mutate({ id: note.id })}
        >
          <Button kind="circled" size="small" icon={Delete} />
        </WithConfirmation>
      </ActionsContainer>
    </Container>
  );
};
